import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Code,
  FlaskConical,
  Globe,
  Loader2,
  Pencil,
  Sparkles,
  Wrench,
} from "lucide-react";
import { useState } from "react";
import * as z from "zod";
import { Button } from "~/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { STARTER_TEMPLATES, type StarterTemplateId } from "~/lib/onboarding/constants";
import {
  $importStarterTemplates,
  $updateOnboardingStep,
} from "~/lib/onboarding/mutations";

const searchSchema = z.object({
  connectionId: z.string(),
});

const ICON_MAP: Record<string, React.ElementType> = {
  sparkles: Sparkles,
  pencil: Pencil,
  code: Code,
  "flask-conical": FlaskConical,
  wrench: Wrench,
  globe: Globe,
};

const COLOR_MAP: Record<string, string> = {
  blue: "bg-blue-500/10 text-blue-500",
  green: "bg-green-500/10 text-green-500",
  purple: "bg-purple-500/10 text-purple-500",
  orange: "bg-orange-500/10 text-orange-500",
  red: "bg-red-500/10 text-red-500",
  cyan: "bg-cyan-500/10 text-cyan-500",
};

function TemplatesScreen() {
  const navigate = useNavigate();
  const { connectionId } = Route.useSearch();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<StarterTemplateId[]>([]);

  const importMutation = useMutation({
    mutationFn: async () => {
      if (selectedIds.length > 0) {
        await $importStarterTemplates({
          data: {
            templateIds: selectedIds,
            defaultProviderId: connectionId,
          },
        });
      }
    },
  });

  const toggleTemplate = (id: StarterTemplateId) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const selectAll = () => {
    if (selectedIds.length === STARTER_TEMPLATES.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(STARTER_TEMPLATES.map((t) => t.id));
    }
  };

  const handleContinue = async () => {
    await importMutation.mutateAsync();

    await $updateOnboardingStep({ data: { step: 4 } });

    queryClient.invalidateQueries({ queryKey: ["prompt-templates"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
    queryClient.invalidateQueries({ queryKey: ["userProviders"] });
    queryClient.invalidateQueries({ queryKey: ["user"] });

    navigate({ to: "/onboarding/complete" });
  };

  return (
    <div className="flex flex-col items-center">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold tracking-tight">
          Choose your starter templates
        </h1>
        <p className="text-muted-foreground">
          Select prompts to add to your collection. You can add more later.
        </p>
      </div>

      <div className="mb-6 grid w-full grid-cols-2 gap-4 md:grid-cols-3">
        {STARTER_TEMPLATES.map((template) => {
          const Icon = ICON_MAP[template.icon] || Sparkles;
          const colorClass = COLOR_MAP[template.color] || COLOR_MAP.blue;
          const isSelected = selectedIds.includes(template.id);

          return (
            <Card
              key={template.id}
              className={`hover:border-primary/50 cursor-pointer transition-all ${
                isSelected ? "border-primary ring-primary/20 ring-2" : ""
              }`}
              onClick={() => toggleTemplate(template.id)}
            >
              <CardHeader className="space-y-3">
                <div className="flex items-start justify-between">
                  <div
                    className={`flex size-10 items-center justify-center rounded-lg ${colorClass}`}
                  >
                    <Icon className="size-5" />
                  </div>
                  <Checkbox checked={isSelected} />
                </div>
                <div>
                  <CardTitle className="text-base">{template.name}</CardTitle>
                  <CardDescription className="text-xs">
                    {template.description}
                  </CardDescription>
                </div>
                <div className="text-muted-foreground text-xs">
                  Model: {template.defaultModel}
                </div>
              </CardHeader>
            </Card>
          );
        })}
      </div>

      <div className="flex w-full max-w-md flex-col gap-3">
        <Button variant="outline" onClick={selectAll}>
          {selectedIds.length === STARTER_TEMPLATES.length
            ? "Deselect All"
            : "Select All"}
        </Button>
        <Button onClick={handleContinue} disabled={importMutation.isPending}>
          {importMutation.isPending ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Importing...
            </>
          ) : selectedIds.length > 0 ? (
            `Continue with ${selectedIds.length} template${selectedIds.length > 1 ? "s" : ""}`
          ) : (
            "Skip & Continue"
          )}
        </Button>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/(authenticated)/onboarding/templates")({
  component: TemplatesScreen,
  validateSearch: searchSchema,
});
